/**
 * Shared display helpers for shipment / piece status values.
 *
 * Backend rollup adds two new partial states to the existing five terminal
 * states (see FRONTEND_INTEGRATION_GUIDE §9):
 *
 *  - `partially_shipped` (some pieces shipped, others not)
 *  - `partially_delivered` (some delivered, others not)
 *
 * They render as "striped" / "half-filled" chips so users can tell them apart
 * from the terminal states at a glance.
 */

/** Normalize backend status into a snake_case-ish key for switch tables. */
export function normalizeStatusKey(status) {
  if (status == null) return '';
  return String(status)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
}

/**
 * i18n key for a status (uses the existing `shipmentPieces.status_*` namespace
 * so a single chip can render piece + parent shipment values uniformly).
 */
export function shipmentStatusLabelKey(status) {
  const key = normalizeStatusKey(status);
  switch (key) {
    case 'pending':
      return 'shipmentPieces.status_pending';
    case 'shipping_soon':
      return 'shipmentPieces.status_shippingSoon';
    case 'shipped':
      return 'shipmentPieces.status_shipped';
    case 'arrived':
      return 'shipmentPieces.status_arrived';
    case 'out_for_delivery':
      return 'shipmentPieces.status_outForDelivery';
    case 'delivered':
      return 'shipmentPieces.status_delivered';
    case 'in_transit':
      return 'shipmentPieces.status_inTransit';
    case 'partially_shipped':
      return 'shipmentPieces.status_partiallyShipped';
    case 'partially_delivered':
      return 'shipmentPieces.status_partiallyDelivered';
    default:
      return null;
  }
}

/**
 * Joy UI palette color for a status. Used by Chip variant=soft.
 *
 *  - delivered → success
 *  - partially_delivered / out_for_delivery → primary
 *  - shipped / partially_shipped / arrived / in_transit → warning
 *  - everything else → neutral
 */
export function shipmentStatusJoyColor(status) {
  switch (normalizeStatusKey(status)) {
    case 'delivered':
      return 'success';
    case 'partially_delivered':
    case 'out_for_delivery':
      return 'primary';
    case 'shipped':
    case 'partially_shipped':
    case 'arrived':
    case 'in_transit':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** True for the new `partially_*` rollup states. */
export function isPartialShipmentStatus(status) {
  const key = normalizeStatusKey(status);
  return key === 'partially_shipped' || key === 'partially_delivered';
}

/**
 * sx for a `<Chip />` (Joy UI) that visually marks partial states with a
 * subtle diagonal stripe pattern. The pattern color follows the chip's
 * background; we use semi-transparent white for the stripes so it works on
 * any soft palette.
 */
export function shipmentStatusChipSx(status) {
  if (!isPartialShipmentStatus(status)) return undefined;
  return {
    backgroundImage:
      'repeating-linear-gradient(135deg, rgba(255,255,255,0.55) 0 6px, transparent 6px 12px)',
    backgroundBlendMode: 'overlay',
    fontStyle: 'italic',
    border: '1px dashed currentColor',
  };
}

/** Returns the translated label or the raw status when no i18n key exists. */
export function shipmentStatusLabel(status, t) {
  const key = shipmentStatusLabelKey(status);
  if (key && typeof t === 'function') return t(key);
  return status == null || status === '' ? '—' : String(status);
}

/** All known status values (used to populate dropdowns / filters). */
export const ALL_SHIPMENT_STATUSES = [
  'shipping soon',
  'shipped',
  'partially_shipped',
  'arrived',
  'in_transit',
  'out for delivery',
  'partially_delivered',
  'delivered',
  'pending',
];
